import React, { useState, useMemo } from "react";
import Condition from "./condition";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import Variables from "@/workflow_api/components/variables";
import { ConditionProvider, useCondition } from "./useCondition";
import {
  useRenameConnectionLabel,
  useUpdateNode,
} from "@/workflow_api/services/nodes.api";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { Icon } from "@elaniintc/goes-design-system";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import type { NodeVariable } from "../types";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";
import Empty from "@/workflow_api/components/variables/empty";
import { InputOutput } from "@/workflow/components/InputOutput";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

interface Props {
  initialTitle?: string;
}

export const Wrapper: React.FC<Props> = ({ initialTitle = "Operador IF" }) => {
  const { variables: nodeVariables, isLoading } = useGetVariables();
  const { flowId, getNodeById, getInteroperabilityNodesUpstreamFromIf } =
    useWorkflowStore();
  const { mutateAsync: updateNode, isPending: isLoadingUpdate } =
    useUpdateNode(flowId);
  const {
    getSerializedNode,
    isAddConditionDisabled,
    selectedVariable,
    trueLabel,
    falseLabel,
    areLabelsValid,
    selectedOperation,
  } = useCondition();
  const { mutateAsync: renameConnectionLabel } =
    useRenameConnectionLabel(flowId);

  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();

  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;

  const [title, setTitle] = useState<string>(() => {
    return nodeData?.displayName || initialTitle;
  });

  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } = useNodeNameValidation(selectedNodeId);

  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  // Check if the condition is checking the "Resultado" variable
  const isCheckingResultado = useMemo(() => {
    if (!selectedVariable) return false;
    // Check if the variable name is "Resultado" or "resultado" (case insensitive)
    return selectedVariable.name?.toLowerCase() === "resultado";
  }, [selectedVariable]);

  // Get interoperability nodes upstream from IF node when checking Resultado
  const interoperabilityNodes = useMemo(() => {
    if (!isCheckingResultado || !selectedNodeId) return [];
    return getInteroperabilityNodesUpstreamFromIf(selectedNodeId);
  }, [
    isCheckingResultado,
    selectedNodeId,
    getInteroperabilityNodesUpstreamFromIf,
  ]);

  const onSave = async () => {
    if (!selectedNodeId) return;
    if (!selectedNode) return;

    const serializedInputs = getSerializedNode(nodeVariables);

    try {
      const updatedNode = await updateNode({
        node_id: selectedNodeId,
        node: {
          flow_id: flowId,
          ref_name: nodeData.type as RefName,
          display_name: title,
          order: nodeData.order,
          position_x: selectedNode.position.x,
          position_y: selectedNode.position.y,
          type: nodeData.node_type,
          manual_continue: manualConfirmation,
          inputs: serializedInputs,
        },
      });
      if (trueLabel) {
        // search connextion with ref_name 'true'
        const trueConnection = updatedNode.data.target_connections?.find(
          (conn) => {
            return conn.ref_name === "true";
          },
        );
        if (trueConnection && trueLabel !== trueConnection.display_name) {
          await renameConnectionLabel({
            connectionId: trueConnection.id,
            label: trueLabel,
          });
        }
      }

      if (falseLabel) {
        // search connextion with ref_name 'false'
        const falseConnection = updatedNode.data.target_connections?.find(
          (conn) => {
            return conn.ref_name === "false";
          },
        );
        if (falseConnection && falseLabel !== falseConnection.display_name) {
          await renameConnectionLabel({
            connectionId: falseConnection.id,
            label: falseLabel,
          });
        }
      }
    } catch (error) {
      console.error("Error updating node:", error);
    }
    setNodeDetailsOpen(false);
    setSelectedNodeId();
  };

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={!isAddConditionDisabled && areLabelsValid && isNameValid(title)}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          onSave();
        }}
        isLoading={isLoadingUpdate}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
          }}
          centerHeader={{
            icon: "split",
            title: title,
            color: "neutral",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            showManualConfirmation: true,
            manualConfirmation: manualConfirmation,
            onManualConfirmationChange: (checked) => {
              console.log("Confirmación manual (Operador IF):", checked);
              setManualConfirmation(checked);
            },
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          centerContent={
            !isLoading ? (
              <Content variables={nodeVariables} />
            ) : (
              <div>
                <p className="min-h-12 text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-xl text-body"
                  />
                </p>
              </div>
            )
          }
          rightContent={
            <>
              {isCheckingResultado && interoperabilityNodes.length > 0 ? (
                <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                  {interoperabilityNodes.map((interopNode) => {
                    const interopNodeData =
                      interopNode.data as unknown as NodeData;
                    // Extract serviceId and endpointId from interoperability node inputs
                    const serviceInput = interopNodeData.inputs?.find(
                      (input) => input.name === "service",
                    );
                    const endpointInput = interopNodeData.inputs?.find(
                      (input) => input.name === "action",
                    );
                    const serviceId = serviceInput?.value as string | undefined;
                    const endpointId = endpointInput?.value as
                      | string
                      | undefined;

                    return (
                      <InputOutput
                        key={interopNode.id}
                        nodeType={NodeTypes.INTEROPERABILITY}
                        nodeLabel={interopNodeData.displayName || ""}
                        showSteps={false}
                        variableName=""
                        variableType="text"
                        params={{
                          serviceId,
                          endpointId,
                        }}
                      />
                    );
                  })}
                </div>
              ) : selectedOperation ? (
                <InputOutput
                  nodeType={NodeTypes.IF}
                  nodeLabel={title}
                  showSteps={false}
                  variableName="resultado"
                  variableType="boolean"
                  params={{}}
                />
              ) : (
                <Empty />
              )}
            </>
          }
        />
      </FullModal>
    </>
  );
};

interface ContentProps {
  variables: NodeVariable[];
}

export const Content = ({ variables }: ContentProps) => {
  return <Condition variables={variables} />;
};

export const IfDetails = () => {
  const { getNodeById } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;
  return (
    <ConditionProvider
      inputs={nodeData.inputs || []}
      connections={nodeData.connections}
    >
      <Wrapper initialTitle={nodeData.displayName} />
    </ConditionProvider>
  );
};
